/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { ChevronDown, Menu, X, Check, ArrowRight } from 'lucide-react';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { label: 'How It Works', id: 'what-spring-is' },
    { label: 'The Springboard', action: () => navigate('/springboard') },
    { label: 'Who It\'s For', id: 'pathways' },
    { label: 'Pricing', action: () => navigate('/pricing') },
    { label: 'Youth Foundation', id: 'youth' },
    { label: 'About', id: 'about' },
    { label: 'Questions', id: 'faq' },
  ];

  /* ---- Section 6: What SPRING actually is — 3 cards ---- */
  const whatSpringCards = [
    { heading: 'build something new', body: "Turn an idea into a business you own. No idea yet? Good. That's the normal starting point, and finding one is literally what the early stages are for." },
    { heading: 'go it alone, on purpose', body: "Turn what you already know into your own consultancy or sole trader business. Here's the bit nobody tells you: you don't need a huge market. And it's closer than you think." },
    { heading: 'stretch back into the workforce', body: "Go back to employment, but go back different. More visible, more confident, clearer on your worth. Choosing stability isn't settling. Sometimes it's the smartest move on the board, and we'll treat it that way by exploring different avenues and strengths to capture new opportunities." },
  ];

  /* ---- Section 7: Pathway diagram ---- */
  const pathwayDiagram = [
    { name: 'Pathway A', subtitle: 'The Founders Track', color: '#1E491A', phases: ['Find Your Path', 'Make It Real', 'Build, Fund & Grow', 'Scale With Confidence'] },
    { name: 'Pathway B', subtitle: 'The Employment Track', color: '#1E491A', phases: ['Know Your Strengths', 'Find Your Direction', 'Get Job Ready', 'Go After It'] },
    { name: 'Pathway C', subtitle: 'The Independent Track', color: '#1E491A', phases: ['Find Your Expertise', 'Build Your Foundation', 'Win Your First Clients', 'Grow On Your Terms'] },
  ];

  /* ---- FAQs ---- */
  const faqs = [
    { q: 'Is it really free?', a: "Yes. Really. No card, no obligation, no sales call afterwards. The full programme will be a paid service when it opens. The assessment is free for everyone, always." },
    { q: 'Can I start the full programme today?', a: "Not quite yet, and we'd rather tell you that plainly than pretend. The guided stages open to founding members soon, and places go first to people who've completed the assessment. Do it now and you're at the front of the queue." },
    { q: "I don't have a business idea. Is that a problem?", a: "Not even slightly. Most people arrive without one. The assessment works out what you're bringing to the table, and if building a business turns out to be your path, finding the idea is what Stage 2 is for." },
    { q: 'What if it says I should go back to a job?', a: "Then that's worth knowing, isn't it? And it's not a consolation prize. For some people, at some moments, the strongest move is going back to work clearer, more confident and better positioned than before. Either way, it's a suggestion based on what you've told us. The decision stays yours." },
    { q: 'What happens to my answers?', a: "They're used to build your results and to improve the assessment while we test. Held securely under the NZ Privacy Act 2020. Never sold. And if you want them deleted, ask and it's done." },
    { q: 'How long does it really take?', a: "About 20 minutes. Make a cuppa, find a quiet spot, and answer honestly. It's only as useful as what you put in." },
  ];

  /* ---- Platform features (NOT IN DECK — kept as-is) ---- */
  const platformFeatures = [
    { name: 'A guided path', body: 'Nine clear stages, so you always know your next step.' },
    { name: 'Your AI guide, always on', body: 'Support between sessions, day or night, that remembers where you are.' },
    { name: 'Real human mentors', body: 'A person on call when the stakes are real, not just a bot.' },
    { name: 'Honest guidance', body: 'Your guide tells you what you need to hear, not only what you want to hear.' },
    { name: 'One workspace', body: 'Tasks, documents, calendar and progress in a single place.' },
    { name: 'Built around you', body: 'Personalise your Springboard with your own brand, colours and logo.' },
    { name: 'Bring your team', body: 'Add seats and control what each person sees.' },
    { name: 'Made for New Zealanders first', body: 'Built for our market, then Australia and the United Kingdom.' },
    { name: 'Your data, protected', body: 'Held securely, with privacy built in from the start.' },
  ];

  /* ---- Comparison (NOT IN DECK — kept as-is) ---- */
  const comparison = {
    alone: [ "No structure, you don't know what comes next", 'No accountability, easy to stall or stop completely', 'Expensive mistakes nobody warned you about', 'Every hard decision made in isolation', 'No system, scattered files and scattered thinking' ],
    spring: [ '9 stage pathway, always know what comes next', 'Brian is with you and keeps you moving at every step', 'Expertise built in, no expensive mistakes', 'Never alone, Brian is alongside you every step', 'Everything in one place, no equity taken' ],
  };
  const goItAloneBenefits = [
    'A clear path from idea to income, broken into stages you can actually follow.',
    'A guide that is always on, so help is there at 11pm on a Sunday, not just in a scheduled call.',
    'A human mentor on call when the stakes are real.',
    'Honest feedback that protects you from costly mistakes.',
    'A community of New Zealanders doing the same brave thing.',
    'One place that holds everything, so nothing falls through the cracks.',
  ];

  /* ---- Youth Foundation (NOT IN DECK — kept as-is) ---- */
  const youthCards = [
    { name: 'Youth Foundation Programme', body: 'A dedicated track for young New Zealanders who want to build something but do not have the structure or support to do it. The same proven SPRING methodology, adapted for early career entrepreneurs.' },
    { name: 'Mentoring and Community', body: 'Young founders are paired with experienced SPRING mentors. People who have made the same transition and know what the hard moments feel like. No one does this alone.' },
    { name: 'Direct Youth Support Funding', body: 'A share of every dollar SPRING earns goes directly to organisations supporting young people in education, employment and entrepreneurship across New Zealand.' },
  ];

  /* Gold period/full stop */
  const GoldDot = ({ className = '' }) => (
    <span className={className} style={{ color: '#F5A800' }}>.</span>
  );

  return (
    <div className="home-page" id="top">

      {/* ====== Announcement Bar ====== */}
      <div className="announcement-bar" data-testid="announcement-bar">
        You're early. We're still testing SPRING, which means the assessment is free and what you tell us genuinely shapes what we build.
      </div>

      {/* Navigation */}
      <nav className={`home-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-inner">
          <a href="#top" className="nav-logo" onClick={(e) => { e.preventDefault(); scrollToSection('top'); }}>
            spring<GoldDot />
          </a>
          <ul className={`nav-links ${mobileMenuOpen ? 'open' : ''}`}>
            {navLinks.map((link, idx) => (
              <li key={idx}>
                <a href={link.id ? `#${link.id}` : '#'} onClick={(e) => { e.preventDefault(); link.action ? link.action() : scrollToSection(link.id); }}>{link.label}</a>
              </li>
            ))}
          </ul>
          <button className="nav-cta" onClick={() => user ? navigate('/portal') : navigate('/assessment')} data-testid="nav-get-started">
            {user ? 'The Springboard' : 'Start Free Assessment'}
          </button>
          <button className="nav-mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X color="#fff" /> : <Menu color="#fff" />}
          </button>
        </div>
      </nav>

      {/* ====== HERO — THE FRONT PAGE ====== */}
      <section className="hero-section">
        <div className="hero-canvas">
          {/* Tree — right side */}
          <div className="hero-tree" data-testid="hero-tree">
            <img src="/SPRING_tree_circuit_navy.webp" alt="SPRING tree" />
          </div>

          {/* White line — cuts through the tree at trunk/roots junction */}
          <hr className="hero-rule" />

          {/* Wordmark + tagline — left, above the line */}
          <div className="hero-wordmark">
            <span className="hero-wordmark-text">spring<span className="wm-dot">.</span></span>
            <p className="hero-tagline">An AI and mentoring platform for capability, retention and innovation in Aotearoa's workforce ready to create, launch, and run a business that's truly yours.</p>
            <p className="hero-tagline-accent">The tools. The structure. The support. All in one place.</p>
          </div>

          {/* Signature — left, below the line */}
          <span className="hero-signature">Every New Zealander deserves a way forward</span>

          {/* "Lost your job" copy — left, above the buttons */}
          <p className="hero-subline">Lost your job? About to walk away from one? Either way, the question's the same: what now?</p>

          {/* CTA buttons — bottom left */}
          <div className="hero-ctas">
            <button className="btn-primary" onClick={() => navigate('/assessment')} data-testid="hero-get-started">
              Start the free assessment <ArrowRight className="btn-icon" size={18} />
            </button>
            <button className="btn-secondary" onClick={() => scrollToSection('what-spring-is')}>
              Learn more
            </button>
          </div>
          <p className="hero-under-btn">No cost. No card. No judgement.</p>
        </div>

        {/* Ticker */}
        <div className="ticker-bar">
          <div className="ticker-track">
            {['9-stage structured programme', 'Meet Brian, your guide from day one', 'Access to Human Mentor', 'Stage-by-stage commitment', 'Built for New Zealand', 'Your pace, your path', 'Proud to give back'].map((item, i) => (
              <span key={i} className="ticker-item">✦ {item}</span>
            ))}
            {['9-stage structured programme', 'Meet Brian, your guide from day one', 'Access to Human Mentor', 'Stage-by-stage commitment', 'Built for New Zealand', 'Your pace, your path', 'Proud to give back'].map((item, i) => (
              <span key={`dup-${i}`} className="ticker-item">✦ {item}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ====== Section 3 — However you got here ====== */}
      <section className="section-white">
        <div className="section-inner">
          <div className="section-with-illustration">
            <div className="section-text-side">
              <h2 className="section-headline" data-testid="however-heading">however you got here</h2>
              <p className="section-body">Maybe it's already happened. The meeting where they thanked you for everything with that goodbye smile. The box. The car park. And the next morning, the alarm you don't need any more going off anyway.</p>
              <p className="section-body">Maybe it's coming. The restructure email. The consultation period. Weeks of turning up and doing the job while quietly wondering when that email is going to hit, doing the mortgage and household maths under the desk.</p>
              <p className="section-body">Or maybe nothing dramatic happened at all. Maybe it's the Sunday night dread of a role you've outgrown or hate. Maybe the payslip that used to be enough quietly isn't anymore, and you've been thinking there has to be more than one income from one employer. Maybe you just know, that you've got more in you.</p>
              <p className="section-body">Different doors. Same question. What now?</p>
              <p className="section-body">And here's what we at SPRING would say to you... whichever door you came through: you're not broken, and you're not short on ability. You're a capable person standing between chapters, with years of skill and knowledge looking for their next way forward. The last chapter is written. The next one isn't. That's not a crisis. That's a blank page.</p>
            </div>
            <div className="section-illustration-side">
              <img src="/illustration_doors.jpg" alt="Three doors — different ways people arrive" className="section-illustration" />
            </div>
          </div>
        </div>
      </section>

      {/* ====== Section 4 — Why you feel stuck ====== */}
      <section className="section-white">
        <div className="section-inner">
          <div className="section-with-illustration">
            <div className="section-illustration-side">
              <img src="/illustration_donkey.jpg" alt="The donkey between carrots and water" className="section-illustration" />
            </div>
            <div className="section-text-side">
              <h2 className="section-headline" data-testid="stuck-heading">why you feel stuck (and why it's not a character flaw)</h2>
              <p className="section-body">There's an old story about a donkey standing exactly halfway between a pile of carrots and a bucket of water. He's hungry and he's thirsty. But every step toward the water takes him further from the carrots, and every step toward the carrots takes him further from the water. So he stands there. And he starves.</p>
              <p className="section-body">Here's what the donkey never worked out. His problem was never the distance. It was believing he had to choose one forever. He didn't. Water first. Then carrots. One honest step, then the next. Standing still felt like keeping both options open. It was the only choice that lost both.</p>
              <p className="section-body">Sound familiar? Stuck almost always means wanting two things at once that can't both be true. I want something of my own, but I can't lose the security. I want to start again, but I never want to feel this exposed again. That little word but is doing all the damage, and no amount of willpower gets you past it.</p>
              <p className="section-body">You know what does? Naming it. Not &ldquo;I'm stuck.&rdquo; The actual thing. The exact fear, and the exact trade off is the exact voice in your head. Once you can see what's holding you in place, you can finally make an honest decision about it. That's a skill. It can be learned. It's where SPRING begins.</p>
              <p className="section-body">And if what's holding you there is everything you poured into the old career, try looking at it this way. That career was a gift from your former self. You can be grateful for it, keep every skill it gave you, and use it. That's not quitting on yourself. That's backing yourself.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ====== Section 5 — You're going to earn again ====== */}
      <section className="earn-again-section">
        <div className="section-inner">
          <div className="earn-again-content">
            <div className="earn-again-text">
              <h2 className="section-headline" data-testid="earn-again-heading">you're going to earn again</h2>
              <p className="section-body">That's not a pep talk. It's the point of the whole thing. Everything here, the stages, the mentors, the Springboard, exists for one outcome: money coming in again, from work that's yours.</p>
              <p className="section-body">Your skills didn't leave when the job did. Years of expertise, skills and qualifications, solving problems, running teams and delivering under pressure are worth real money to somebody. The journey exists to find out who and how to make that happen, and to get you paid for what you know: sooner than you think.</p>
              <p className="pull-quote" data-testid="earn-again-pullquote">Comfort is a side effect. The destination is income.</p>
            </div>
            <div className="earn-again-illustration">
              <img src="/dog_transparent.png" alt="SPRING companion" className="earn-again-dog" />
            </div>
          </div>
        </div>
      </section>

      {/* ====== Section 6 — What SPRING actually is ====== */}
      <section className="section-navy" id="what-spring-is">
        <div className="section-inner wide">
          <h2 className="section-headline center" data-testid="what-spring-heading">what SPRING actually is</h2>
          <p className="section-body center max-w-2xl mx-auto">It's an online guided journey. In stages, taken one at a time, at your pace, from wherever you are. The platform walks you through every step: what to do, in what order, and why it matters. And you're not doing it alone. Along the way you have access to real human mentors who've done hard things themselves, a community of people walking the same road at the same time as you, and an AI coach available around the clock, because the wobble doesn't keep office hours and sometimes you need someone at 11pm.</p>
          <p className="section-body center max-w-2xl mx-auto">That's the design, and it's deliberate: structure you can follow at midnight in your trackies, and real people to reach for when a decision feels too big to make alone. And there's no single &ldquo;right&rdquo; path here. There are three. The assessment helps you find yours.</p>
          <div className="cards-grid">
            {whatSpringCards.map((card, i) => (
              <div key={i} className="deck-card" data-testid={`spring-card-${i}`}>
                <h4>{card.heading}</h4>
                <p>{card.body}</p>
              </div>
            ))}
          </div>
          <p className="section-body center" style={{ marginTop: 40, fontWeight: 700, color: '#F6ADBC' }}>
            One more thing, whichever path you take. The working world is splitting into people who make AI work for them and people who end up working for it. Every SPRING pathway also helps build real, practical AI skills which will help you in the future.
          </p>
        </div>
      </section>

      {/* ====== Section 7 — Three Pathways ====== */}
      <section className="pathways-journey-section" id="pathways">
        <div className="section-inner wide">
          <h2 className="section-headline center" data-testid="three-pathways-heading">three pathways. it's a staged journey.</h2>
          <p className="section-body center max-w-2xl mx-auto">Three pathways. Three different destinations. One thing they have in common: nobody hands you the whole mountain at once. Whichever path the assessment points you towards, the shape is the same. Four phases, taken in order, each one made up of stages you work through at your own pace. You always know where you are, what you are doing next, and why it matters.</p>
          <p className="section-body center max-w-2xl mx-auto">Pathway A. The Founders track. For people building something of their own. It starts with finding your path and finishes with a business that runs and grows on your terms.</p>
          <p className="section-body center max-w-2xl mx-auto">Pathway B. The Employment track. For people heading back into work, but going back stronger than they left. It starts where you are today and finishes with you ready to go after the roles worth having.</p>
          <p className="section-body center max-w-2xl mx-auto">Pathway C. The Independent track. For people who already have the expertise and want to sell it themselves. It starts with the thing you are genuinely good at, the one somebody would pay you for tomorrow, then builds the foundation, the sales and the growth around it.</p>
          <p className="section-body center max-w-2xl mx-auto" style={{ fontWeight: 700, color: '#F6ADBC' }}>Different roads. Same guidance, same people beside you, same destination in the end: money coming in again, from work that is yours.</p>
          <div className="pathway-diagram" data-testid="pathway-diagram">
            {pathwayDiagram.map((pw, i) => (
              <div key={i} className="pathway-diagram-col">
                <div className="pathway-diagram-header" style={{ background: pw.color }}>
                  <span className="pathway-diagram-name">{pw.name}</span>
                  <span className="pathway-diagram-subtitle">{pw.subtitle}</span>
                </div>
                <div className="pathway-diagram-phases">
                  {pw.phases.map((phase, j) => (
                    <div key={j} className="pathway-diagram-phase">
                      <span className="phase-num">Phase {j + 1}</span>
                      <span className="phase-name">{phase}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="section-body center" style={{ marginTop: 40, fontWeight: 700, color: '#F6ADBC' }}>
            Every stage: clear guidance, access to real mentors, a community walking beside you, and your AI coach on hand around the clock in between.
          </p>
        </div>
      </section>

      {/* ====== Section 8 — The Springboard ====== */}
      <section className="section-white" id="springboard">
        <div className="section-inner">
          <div className="section-with-illustration">
            <div className="section-text-side">
              <h2 className="section-headline center" data-testid="springboard-heading">the springboard</h2>
              <p className="section-body">The journey has an ending. Your momentum shouldn't. The Springboard is the command centre for your new business. One place, with everything in it. Every plan, decision and document from your journey, kept, organised and ready to reuse. A library of templates for the stuff every business needs. Your tasks and next actions laid out, so Monday morning never starts with a blank page. Your mentor conversations in one thread instead of scattered across three inboxes. And your AI coach on hand around the clock.</p>
              <p className="section-body">Here's why that matters. Running a business is ten percent big decisions and ninety percent staying on top of things. The admin. The follow ups. The paperwork that quietly eats new business owners alive. The Springboard works with you on exactly that side of it: neatly stored, properly organised, one login instead of eleven browser tabs. You do the thinking. It keeps the house in order.</p>
              <p className="section-body">And here's the truth about going out on your own: the hardest part isn't the start. It's month seven, when the first excitement has worn off and the discipline has to carry you. The Springboard is your home base from day one, and it stays yours after the programme ends, so nobody does month seven alone, and nobody does month seven drowning in paper either.</p>
            </div>
            <div className="section-illustration-side">
              <img src="/illustration_desk.jpg" alt="Person at the Springboard — calm, in control" className="section-illustration" />
            </div>
          </div>
        </div>
      </section>

      {/* Platform Benefits — NOT IN DECK, kept as-is, restyled */}
      <section className="section-navy platform-benefits-section" id="benefits">
        <div className="section-inner wide">
          <h2 className="section-headline center" data-testid="benefits-heading">what the platform gives you</h2>
          <div className="benefits-grid">
            {platformFeatures.map((feat, i) => (
              <div key={i} className="benefit-card" data-testid={`benefit-card-${i}`}>
                <h4 className="benefit-name">{feat.name}</h4>
                <p className="benefit-body">{feat.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison — NOT IN DECK, kept as-is, restyled */}
      <section className="comparison-section">
        <div className="section-inner wide">
          <h2 className="section-headline center">most people go it alone.<br/><span className="text-muted-white">SPRING exists so you never have to.</span></h2>
          <ul className="go-it-alone-benefits" data-testid="go-it-alone-benefits">
            {goItAloneBenefits.map((benefit, i) => (
              <li key={i}><Check style={{ color: '#F6ADBC', flexShrink: 0 }} size={20} /> {benefit}</li>
            ))}
          </ul>
          <div className="comparison-grid">
            <div className="comparison-col alone">
              <h4 className="col-label">Going it alone</h4>
              <ul>{comparison.alone.map((item, i) => <li key={i}><X className="icon-x" size={18} /> {item}</li>)}</ul>
            </div>
            <div className="comparison-col spring">
              <h4 className="col-label">SPRING</h4>
              <ul>{comparison.spring.map((item, i) => <li key={i}><Check className="icon-check" size={18} /> {item}</li>)}</ul>
            </div>
          </div>
        </div>
      </section>

      {/* ====== Section 9 — The free assessment ====== */}
      <section className="free-assessment-section" id="assessment">
        <div className="section-inner">
          <h2 className="section-headline center" data-testid="free-assessment-heading">the free assessment</h2>
          <p className="section-body center max-w-2xl mx-auto">Here's how it works. A structured conversation across four areas: your mindset, what actually matters to you, your money situation, and the skills you've genuinely built. Answer honestly and it hands you back a clear picture of where you stand, and which path fits the person you actually are. Not the person a brochure imagines.</p>
          <p className="section-body center max-w-2xl mx-auto" style={{ fontWeight: 700, color: '#F6ADBC' }}>Three things before you start:</p>
          <div className="assessment-points">
            <p className="section-body center max-w-2xl mx-auto">It's a starting point, not a verdict. Nothing on earth can predict whether you'll succeed, and we won't pretend otherwise. What it does is help you see your own situation clearly. Clarity first. Decisions second.</p>
            <p className="section-body center max-w-2xl mx-auto">We make a suggestion. You make the choice. Always in that order.</p>
            <p className="section-body center max-w-2xl mx-auto">It's free. Actually free. No card, no catch, no sales call. Your results are yours to keep.</p>
          </div>
          <div className="center-cta">
            <button className="btn-primary" onClick={() => navigate('/assessment')} data-testid="assessment-cta">
              Start the free assessment <ArrowRight className="btn-icon" size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ====== Section 10 — Who's behind this ====== */}
      <section className="about-section" id="about">
        <div className="section-inner">
          <div className="about-content">
            <h2 className="section-headline" data-testid="about-heading">who's behind this</h2>
            <p className="section-body">Two founders. Forty plus years of combined experience across business, operations and people. And one moment we couldn't unsee: capable New Zealanders, good at their jobs, walking out the door with a payout, a departure date, and nothing else. No plan. No guide. Nothing. So we built the thing we thought should exist.</p>
            <p className="section-body">We're a give back organisation, and we're unashamedly for New Zealand. We want Kiwi talent staying here, building here. As SPRING grows we'll back New Zealand charities, sponsor a Youth version for young people who haven't landed their first job yet, and build a dedicated Te Reo Māori version of the programme.</p>
            <p className="section-body" style={{ fontWeight: 700 }}>And a few things we simply believe about people:</p>
            <ul className="beliefs-list" data-testid="beliefs-list">
              <li>Everyone has potential. Everyone. We've never met an exception.</li>
              <li>We listen properly. To what you say, and to what you're not saying.</li>
              <li>We don't judge. Where you are right now is a situation. It's not who you are.</li>
              <li>We speak your language. No jargon. No lectures. And absolutely no pity.</li>
            </ul>
          </div>
          <div className="team-grid">
            <div className="team-card">
              <h4 className="team-name">Louise Stodart</h4>
              <p className="team-title">Founder</p>
              <p className="team-bio">Twenty three years of working with people in business has taught me one thing above everything else. The moments of change are the moments that define us. I built SPRING because I believe every single person deserves the chance to find their path.</p>
            </div>
            <div className="team-card">
              <h4 className="team-name">Richard Hollingum</h4>
              <p className="team-title">Founder</p>
              <p className="team-bio">I have spent my career making things happen alongside people who made the same transition and know what the hard moments feel like. I built SPRING because I believe that moment should be available to everyone.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ====== Section 11 — Being early ====== */}
      <section className="being-early-section">
        <div className="section-inner">
          <h2 className="section-headline center" data-testid="being-early-heading">being early</h2>
          <p className="section-body center max-w-2xl mx-auto">Can we be straight with you? You're early. Everything you've just read about the journey and the Springboard is built and being tested; it opens to founding members soon. What's open right now is the free assessment, and your run through it helps us test three things: does the platform hold up, does the assessment read you accurately, and does what we're offering actually land.</p>
          <p className="section-body center max-w-2xl mx-auto">What's in it for you? Your full results, first in line when the programme opens, and a straight line to the founders. If anything feels off, confusing or just plain wrong, tell us: hello@springcollective.biz. We answer, and we act on it.</p>
        </div>
      </section>

      {/* Youth Foundation — NOT IN DECK, kept as-is, restyled */}
      <section className="youth-section" id="youth">
        <div className="section-inner wide">
          <h2 className="section-headline center">building futures.<br/><span className="text-primary">not just businesses.</span></h2>
          <p className="section-body center max-w-2xl mx-auto" style={{ color: '#595959' }}>When one person moves forward, others move with them. That is the ripple effect SPRING believes in. A family changes. A community shifts. And slowly, so does a country.</p>
          <p className="section-body center max-w-2xl mx-auto" style={{ color: '#595959' }}>Through the SPRING Youth Foundation Programme, we invest in young New Zealanders who deserve the same structured support that powers our core programme.</p>
          <div className="youth-grid">
            {youthCards.map((card, i) => (
              <div key={i} className="youth-card">
                <h4 className="youth-name">{card.name}</h4>
                <p className="youth-body">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== Section 12 — FAQs ====== */}
      <section className="faq-section" id="faq">
        <div className="section-inner">
          <h2 className="section-headline center" data-testid="faq-heading">questions people actually ask</h2>
          <div className="faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`faq-item ${openFaq === i ? 'open' : ''}`}>
                <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`faq-q-${i}`}>
                  {faq.q}
                  <ChevronDown className="faq-icon" size={20} />
                </button>
                {openFaq === i && <div className="faq-a">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== Section 13 — Final CTA ====== */}
      <section className="final-cta-section">
        <div className="section-inner center">
          <h2 className="section-headline" style={{ color: '#FFFFFF' }} data-testid="final-cta-heading">
            some of you didn't choose this moment.<br/>all of you get to choose the next one.
          </h2>
          <button className="btn-primary" onClick={() => navigate('/assessment')} data-testid="final-cta">
            Start the free assessment <ArrowRight className="btn-icon" size={18} />
          </button>
        </div>
      </section>

      {/* ====== Page footer elements ====== */}
      <div className="page-footer-elements">
        <span className="page-wordmark-sm">spring<span className="wm-dot-sm">.</span></span>
        <span className="page-signature-sm">Every New Zealander deserves a way forward</span>
      </div>

      {/* ====== Footer ====== */}
      <footer className="home-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <p>hello@springcollective.biz</p>
            <p>The Spring Collective Biz Ltd, Auckland, New Zealand</p>
          </div>
          <div className="footer-links">
            <a href="/privacy">Privacy policy</a>
            <a href="/terms">Terms of use</a>
          </div>
          <div className="footer-copy">
            <p className="footer-copyright">© Spring Collective Limited 2026. All rights reserved</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
